import React, { useMemo } from 'react';
import { SketchAxes } from '../sketch/index.jsx';
import { axisTicks, projectPoint, roughPath, seriesBounds, seriesDates } from '../../utils/chart.js';

/**
 * The plot area in SVG user units. The viewBox scales to whatever width the
 * container has, so these are just a convenient aspect ratio to draw in.
 */
const WIDTH = 300;
const HEIGHT = 150;

/**
 * Colours for the lines, cycled. All four are theme variables, so the chart
 * follows a palette change like everything else.
 */
const STROKES = [
  'rgb(var(--color-accent))',
  'rgb(var(--color-done))',
  'rgb(var(--color-ink))',
  'rgb(var(--color-ink-soft))',
];

/**
 * Multi-line chart drawn as a single hand-inked SVG — no charting library.
 *
 * Sessions are spaced evenly by order rather than by calendar distance, so a
 * fortnight off does not squash the rest of the history into a corner.
 *
 * @param {{id: string, label: string, points: {date: string, value: number}[]}[]} series
 * @param {string[]} [hidden] ids of lines toggled off in the legend
 */
export default function LineChart({ series, hidden = [] }) {
  // Geometry is derived in one memo — `visible` is rebuilt inside it so the
  // dependency list stays on the real inputs rather than a fresh array every
  // render, which would defeat the memo entirely.
  const { dates, labels, paths, ticks } = useMemo(() => {
    const visible = series.filter((line) => !hidden.includes(line.id) && line.points.length > 0);
    const nextBounds = seriesBounds(visible);
    const nextDates = seriesDates(visible);
    const frame = { dates: nextDates, bounds: nextBounds, width: WIDTH, height: HEIGHT };

    return {
      dates: nextDates,
      labels: visible.map((line) => line.label),
      ticks: axisTicks(nextBounds, 3),
      paths: visible.map((line) => {
        const coords = line.points.map((point) => projectPoint(point, frame));
        // Colour by position in the full series list, so hiding a line never
        // recolours the ones still on screen.
        const index = series.indexOf(line);
        return {
          id: line.id,
          // Seeding off the index keeps each line's tremor distinct.
          d: roughPath(coords, 1.4, index * 17 + 3),
          coords,
          stroke: STROKES[index % STROKES.length],
        };
      }),
    };
  }, [series, hidden]);

  return (
    <div className="flex gap-2">
      {/* Y axis labels sit outside the SVG so they never scale with it */}
      <div
        className="flex shrink-0 flex-col justify-between py-0.5 text-right font-hand text-sm
                   leading-none text-ink-soft"
        style={{ height: '11rem' }}
        aria-hidden="true"
      >
        {ticks.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>

      <div className="relative min-w-0 flex-1">
        <SketchAxes className="text-line" />

        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`Chart of ${labels.join(', ')} over ${dates.length} sessions`}
          className="h-44 w-full overflow-visible"
        >
          {/* Faint horizontal rules, one per y-axis label */}
          {ticks.map((tick, index) => (
            <line
              key={tick}
              x1="0"
              x2={WIDTH}
              y1={(index / (ticks.length - 1)) * HEIGHT}
              y2={(index / (ticks.length - 1)) * HEIGHT}
              stroke="rgb(var(--color-line))"
              strokeWidth="1"
              strokeDasharray="3 5"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {paths.map((path) => (
            <g key={path.id}>
              <path
                d={path.d}
                fill="none"
                stroke={path.stroke}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
              {/* A dot per session, so single-session lines are visible too */}
              {path.coords.map((coord) => (
                <circle
                  key={`${coord.x}-${coord.y}`}
                  cx={coord.x}
                  cy={coord.y}
                  r="2.5"
                  fill="rgb(var(--color-paper))"
                  stroke={path.stroke}
                  strokeWidth="1.8"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </g>
          ))}
        </svg>

        {/* X axis: first and last session, which is all that fits on a phone */}
        <div className="flex justify-between pt-1 font-hand text-sm leading-none text-ink-soft">
          <span>{dates[0] ?? ''}</span>
          {dates.length > 1 && <span>{dates[dates.length - 1]}</span>}
        </div>
      </div>
    </div>
  );
}

/** Palette export so the legend can colour its swatches identically. */
export { STROKES };
